import { FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { ClaimRewardsError, useClaimRewards } from "@/hooks/user/useClaimRewards";

type UseClaimSpinningWheelRewardsProps = {
  onClaimSuccess?: () => void;
  onClaimError?: (error: ClaimRewardsError) => void;
};

/**
 * Hook for claiming Spinning Wheel rewards only.
 * Filters to only show SPINNING_WHEEL campaign type from the claimable/claimable-soon endpoints.
 * Used by the WheelHistory component to show only spinning wheel rewards.
 */
export const useClaimSpinningWheelRewards = ({ onClaimSuccess, onClaimError }: UseClaimSpinningWheelRewardsProps) => {
  const apiHelper = useApi();
  const isClaimsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.CLAIMS_HUB_ENABLE);

  return useClaimRewards({
    onClaimSuccess,
    onClaimError,
    queryKeys: {
      claimableRewards: QueryKeys.HUB_REWARDS_CLAIMABLE_SPINNING_WHEEL,
      claimableSoonRewards: QueryKeys.HUB_REWARDS_CLAIMABLE_SOON_SPINNING_WHEEL,
    },
    fetchers: {
      getClaimableRewards: async (userAddress) => {
        // Backend now filters by campaignTypes - no client-side filtering needed
        const claimableRewards = await apiHelper.getOrThrow(API_ENDPOINT.HUB_REWARDS_CLAIMABLE, {
          queryParams: { defiAddress: userAddress, campaignTypes: "SPINNING_WHEEL" },
        });

        return claimableRewards.claimableEpochs;
      },
      simulateClaim: async (claimItems) => {
        await apiHelper.postOrThrow(API_ENDPOINT.HUB_REWARDS_SIMULATE, {
          bodyParams: { claimItems },
        });
      },
      getClaimableSoonRewards: async (userAddress) => {
        // Backend now filters by campaignTypes - no client-side filtering needed
        const claimableSoonRewards = await apiHelper.getOrThrow(API_ENDPOINT.HUB_REWARDS_CLAIMABLE_SOON, {
          queryParams: { defiAddress: userAddress, campaignTypes: "SPINNING_WHEEL" },
        });

        return claimableSoonRewards.claimableSoonEpochs;
      },
    },
    metricsEvent: METRICS_EVENTS.CLAIM_REWARDS,
    enabled: isClaimsEnabled,
  });
};
